'use client';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import type { FloraAIChat } from '../../types/flora-ai.types';
import { formatFloraChatTimestamp, groupFloraChatsByDate } from '../../utils';
import styles from './FloraConversationsPanel.module.css';

interface FloraConversationsPanelProps {
	panelRef: RefObject<HTMLDivElement | null>;
	isOpen: boolean;
	position: { top: number; left: number } | null;
	chats: FloraAIChat[];
	activeChatId?: string;
	onClose: () => void;
	onSelectChat: (chat: FloraAIChat) => void;
	onNewConversation: () => void;
}

export const FloraConversationsPanel = ({
	panelRef,
	isOpen,
	position,
	chats,
	activeChatId,
	onClose,
	onSelectChat,
	onNewConversation,
}: FloraConversationsPanelProps) => {
	if (!isOpen || !position) return null;

	const groups = groupFloraChatsByDate(chats);

	return createPortal(
		<>
			<div className={styles.overlay} onClick={onClose} role="presentation" />
			<div
				ref={panelRef}
				className={styles.panel}
				style={{ top: position.top, left: position.left }}
				role="dialog"
				aria-label="Conversas"
			>
				<div className={styles.header}>
					<h2 className={styles.title}>Conversas</h2>
					<button
						type="button"
						className={styles.newButton}
						onClick={onNewConversation}
						aria-label="Nova conversa"
					>
						<Plus className={styles.newIcon} strokeWidth={2} />
					</button>
				</div>

				<div className={styles.content}>
					{groups.map((group) => (
						<div key={group.label} className={styles.group}>
							<span className={styles.groupLabel}>{group.label}</span>
							{group.chats.map((chat) => (
								<button
									key={chat.id}
									type="button"
									className={`${styles.item} ${chat.id === activeChatId ? styles.itemActive : ''}`}
									onClick={() => onSelectChat(chat)}
								>
									<span className={styles.itemTitle}>{chat.title}</span>
									{chat.lastMessage && <span className={styles.itemMessage}>{chat.lastMessage}</span>}
									<span className={styles.itemMeta}>
										{formatFloraChatTimestamp(chat.timestamp)} · {chat.dossierCount} no dossiê
									</span>
								</button>
							))}
						</div>
					))}
				</div>
			</div>
		</>,
		document.body,
	);
};
